import { deriveImplementationActivity } from '@trevize/api/modules/implementations/implementation-activity'

import type {
  Run,
  RunExecution,
  RunRepo,
  RunStep,
  RunStepExecution,
  RunWorkflowState,
  Sandbox,
} from '../../zero-schema.gen.ts'

export type RunExecutionWithSteps = RunExecution & {
  stepExecutions?: ReadonlyArray<RunStepExecution> | null
}

export type RunWithProjections = Run & {
  runRepos?: ReadonlyArray<RunRepo> | null
  workflowState?: RunWorkflowState | null
  executions?: ReadonlyArray<RunExecutionWithSteps> | null
  /** Availability hint for the Daytona-only Desktop view on sessions and tasks. */
  sandbox?: Pick<Sandbox, 'provider' | 'currentState'> | null
}

function latestItem<T>(items: ReadonlyArray<T>): T | null {
  return items.length > 0 ? (items[items.length - 1] ?? null) : null
}

function isoTimestamp(value: number | null | undefined): string | null {
  return value === null || value === undefined
    ? null
    : new Date(value).toISOString()
}

export function projectRunWorkflowState(
  currentRun: RunWithProjections | null | undefined,
  steps: ReadonlyArray<Pick<RunStep, 'id' | 'status'>>,
) {
  const workflowState = currentRun?.workflowState ?? null
  const runExecutions = currentRun?.executions ?? []
  const currentRunExecution =
    runExecutions.find((execution) => execution.status === 'running') ??
    latestItem(runExecutions)
  const stepExecutions = runExecutions.flatMap(
    (execution) => execution.stepExecutions ?? [],
  )
  const currentStepExecution = workflowState?.activeStepExecutionId
    ? (stepExecutions.find(
        (execution) => execution.id === workflowState.activeStepExecutionId,
      ) ?? null)
    : latestItem(stepExecutions)
  const currentStep =
    steps.find((step) => step.id === currentRun?.currentStepId) ?? null
  const primaryRepo = currentRun?.runRepos?.[0]
  const derived = deriveImplementationActivity({
    workflowVersion: currentRun?.workflowVersion ?? null,
    runStatus: currentRun?.status ?? 'pending',
    runError: currentRun?.error ?? null,
    enginePhase: workflowState?.enginePhase ?? null,
    currentStepName: currentRun?.currentStepName ?? null,
    currentStepStatus:
      currentStepExecution?.status ?? currentStep?.status ?? null,
    runExecutionStatus: currentRunExecution?.status ?? null,
    suspension: workflowState?.suspension ?? null,
    userHold: workflowState?.userHold ?? null,
    controlRequest: workflowState?.controlRequest ?? null,
    sandboxLifecycle: workflowState?.sandboxLifecycle ?? null,
    executionHealth: workflowState?.executionHealth ?? null,
    failure: workflowState?.failure ?? null,
    branchPushed: primaryRepo?.branchPushed ?? false,
    prStatus: primaryRepo?.prStatus ?? null,
    startedAt: isoTimestamp(currentRun?.startedAt),
    updatedAt:
      isoTimestamp(workflowState?.updatedAt) ??
      isoTimestamp(currentRun?.updatedAt),
  })

  return {
    workflowState,
    runExecutions,
    currentRunExecution,
    stepExecutions,
    currentStepExecution,
    enginePhase: workflowState?.enginePhase ?? null,
    suspension: workflowState?.suspension ?? null,
    userHold: workflowState?.userHold ?? null,
    controlRequest: workflowState?.controlRequest ?? null,
    sandboxLifecycle: workflowState?.sandboxLifecycle ?? null,
    executionHealth: workflowState?.executionHealth ?? null,
    failure: workflowState?.failure ?? null,
    ...derived,
  }
}
